/**
 * Lógica única de presentación de un empleado (fallback de nombre, iniciales, email).
 * La usan tanto el visor común (`app-empleado-display`) como el selector
 * (`app-empleado-autocomplete`) para ser 100% coherentes en toda la app.
 */

export interface EmpleadoLike {
	id?: number | null;
	nombre?: string | null;
	email?: string | null;
	codigo?: string | null;
}

/**
 * Casing de nombre "bonito": minúsculas salvo la inicial de cada palabra
 * (p. ej. "JESUS SANCHEZ" / "adrian campos" → "Jesus Sanchez" / "Adrian Campos").
 * Unicode-aware de verdad: NO usa `\b` (límite de palabra ASCII), que trataba
 * í/ñ/á… como separadores y producía "JosE MaríA" o "MariÑO". Solo capitaliza
 * tras inicio, espacio, guion o apóstrofo. Tampoco vale CSS porque
 * `text-transform: capitalize` no baja las mayúsculas existentes.
 */
export function empleadoTitleCase(s: string): string {
	return (s || "")
		.toLowerCase()
		.replace(
			/(^|[\s\-'’.])(\p{L})/gu,
			(_m, sep: string, letter: string) => sep + letter.toUpperCase(),
		);
}

/** true si el empleado tiene un nombre real (no vacío/espacios). */
export function empleadoTieneNombre(e: EmpleadoLike | null | undefined): boolean {
	return !!(e && e.nombre && e.nombre.trim());
}

/** Email normalizado (trim) o "" si no hay. */
export function empleadoEmail(e: EmpleadoLike | null | undefined): string {
	return (e?.email || "").trim();
}

/**
 * Etiqueta principal con fallback robusto: nombre → email → código → "Empleado #id".
 * Devuelve "" si no hay empleado.
 */
export function empleadoLabel(e: EmpleadoLike | null | undefined): string {
	if (!e) {
		return "";
	}
	const nombre = (e.nombre || "").trim();
	if (nombre) {
		return empleadoTitleCase(nombre);
	}
	const email = (e.email || "").trim();
	if (email) {
		return email;
	}
	const codigo = (e.codigo || "").trim();
	if (codigo) {
		return codigo;
	}
	return e.id ? `Empleado #${e.id}` : "";
}

/** Iniciales para el avatar (del nombre; si no, primera letra del fallback). */
export function empleadoInitials(e: EmpleadoLike | null | undefined): string {
	const nombre = (e?.nombre || "").trim();
	if (nombre) {
		const parts = nombre.split(/\s+/).filter(Boolean);
		const first = parts[0]?.charAt(0) ?? "";
		const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
		return (first + last).toUpperCase() || "?";
	}
	const fb = empleadoLabel(e);
	return fb ? fb.charAt(0).toUpperCase() : "?";
}

/** Deep link para abrir un chat de Teams con la persona (por email). */
export function empleadoTeamsUrl(e: EmpleadoLike | null | undefined): string {
	const email = empleadoEmail(e);
	return email
		? `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(email)}`
		: "";
}
